<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
  rainfall: number | null | undefined;       // 累計雨量 mm
  rainfallRate: number | null | undefined;   // 雨量強度 mm/h
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
});

// 格式化累計雨量
const formattedRainfall = computed(() => {
  if (props.rainfall === null || props.rainfall === undefined) return '0';
  return props.rainfall.toFixed(1);
});

// 格式化雨量強度
const formattedRate = computed(() => {
  if (props.rainfallRate === null || props.rainfallRate === undefined) return '0';
  return props.rainfallRate.toFixed(1);
});

// 計算降雨強度等級
const intensityLevel = computed(() => {
  const rate = props.rainfallRate;
  if (rate === null || rate === undefined) return 'none';
  if (rate === 0) return 'dry';
  if (rate < 2.5) return 'light';
  if (rate < 7.5) return 'moderate';
  if (rate < 15) return 'heavy';
  return 'torrential';
});

// 計算強度等級文字
const intensityText = computed(() => {
  switch (intensityLevel.value) {
    case 'dry': return t('dashboard.weather.rainDry');
    case 'light': return t('dashboard.weather.rainLight');
    case 'moderate': return t('dashboard.weather.rainModerate');
    case 'heavy': return t('dashboard.weather.rainHeavy');
    case 'torrential': return t('dashboard.weather.rainTorrential');
    default: return '-';
  }
});

// 是否正在下雨
const isRaining = computed(() => {
  return props.rainfallRate !== null && props.rainfallRate !== undefined && props.rainfallRate > 0;
});
</script>

<template>
  <div class="power-card rainfall-card">
    <div class="card-header">
      <div class="card-icon">🌧️</div>
      <h3>{{ t('dashboard.weather.rainfall') }}</h3>
    </div>

    <!-- 主要數值：累計雨量 -->
    <div class="card-value">
      {{ formattedRainfall }}<span class="unit">mm</span>
    </div>

    <!-- 副數值：雨量強度 -->
    <div class="sub-value">
      <span class="label">{{ t('dashboard.weather.rainfallRate') }}:</span>
      <span class="value">{{ formattedRate }}</span>
      <span class="unit">mm/h</span>
    </div>

    <!-- 強度等級 -->
    <div v-if="rainfallRate !== null && rainfallRate !== undefined"
         class="efficiency"
         :class="'level-' + intensityLevel">
      {{ intensityText }}
    </div>

    <div class="card-footer">Rainfall Monitor</div>
  </div>
</template>

<style scoped>
.power-card {
  background: rgba(255, 255, 255, 0.08);
  padding: 25px;
  border-radius: 10px;
  border: 2px solid transparent;
  transition: all 0.3s;
  cursor: pointer;
}

.power-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
}

.rainfall-card {
  border-color: #03A9F4;
}

.rainfall-card:hover {
  background: rgba(3, 169, 244, 0.1);
  box-shadow: 0 10px 25px rgba(3, 169, 244, 0.3);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}

.card-icon {
  font-size: 32px;
}

.card-header h3 {
  font-size: 16px;
  color: #b0bec5;
  font-weight: 600;
  margin: 0;
}

.card-value {
  font-size: 48px;
  font-weight: bold;
  color: #ecf0f1;
  line-height: 1.2;
}

.card-value .unit {
  font-size: 18px;
  font-weight: normal;
  color: #90a4ae;
  margin-left: 8px;
}

.sub-value {
  margin-top: 10px;
  font-size: 14px;
  color: #b0bec5;
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.sub-value .label {
  color: #78909c;
}

.sub-value .value {
  font-size: 20px;
  font-weight: 700;
  color: #03A9F4;
}

.sub-value .unit {
  font-size: 12px;
  color: #78909c;
}

.efficiency {
  font-size: 14px;
  padding: 6px 12px;
  border-radius: 6px;
  text-align: center;
  margin-top: 10px;
  display: inline-block;
}

.level-dry {
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
}

.level-light {
  background: rgba(3, 169, 244, 0.2);
  color: #03A9F4;
}

.level-moderate {
  background: rgba(255, 193, 7, 0.2);
  color: #FFC107;
}

.level-heavy {
  background: rgba(255, 152, 0, 0.2);
  color: #FF9800;
}

.level-torrential {
  background: rgba(244, 67, 54, 0.2);
  color: #F44336;
}

.card-footer {
  font-size: 12px;
  color: #78909c;
  margin-top: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
}
</style>
